<script lang="ts">
  import type { FormModalContext, Size } from '$lib/componentTypes';
  import { onMount, setContext, type Snippet } from 'svelte';
  import SpinnerButton from './SpinnerButton.svelte';
  import { invalidate } from '$app/navigation';
  import Modal from '../bootstrap/Modal.svelte';
  import Alert from '../bootstrap/Alert.svelte';
  import { ValidationError } from '$lib/api/types';

  interface Props {
    formId: string;
    /**
     * Called after modal close. Safe to update state that will unmount component with this method
     */
    onclose: () => void;
    /**
     * Called before submitting the form. Perform validation checks and make all controls touched here. Return true if
     * form is validated. Return false if there is a validation error.
     */
    validationChecks?: () => boolean;
    /**
     * Called when the form is submitted. Return true to close modal and perform other cleanup after a successful
     * submit. Return false to reset the form to pre-submit state (e.g. do not close modal, reset spinner).
     */
    onsubmit: () => Promise<boolean>;
    header?: string;
    headerSnippet?: Snippet;
    children?: Snippet;
    onDelete?: () => Promise<void>;
    invalidateUrl?: (url: URL) => boolean;
    deleteConfirmText?: string;
    deleteBtnLabel?: string;
    submitBtnLabel?: string;
    size?: Size;
    scrollable?: boolean;
    showFooter?: boolean;
    /**
     * Custom error handler if the error during `onsubmit` is a ValidationError. Return true if the error is handled.
     * Return false if the error is not handled and should be bubbled up the stack.
     * @param e the validation error
     */
    onvalidationerror?: (e: ValidationError) => boolean;
  }

  let {
    formId,
    onclose,
    onsubmit,
    validationChecks = () => true,
    onvalidationerror = () => false,
    header,
    headerSnippet,
    children,
    onDelete,
    deleteConfirmText,
    deleteBtnLabel = 'Delete',
    submitBtnLabel = 'Save',
    invalidateUrl,
    size = 'md',
    scrollable,
    showFooter = true,
  }: Props = $props();

  let formSubmitErrorAlert: string | null = $state(null);
  let saving = $state(false);
  let confirmDelete = $state(false);

  let modal: Modal;

  function startSaving() {
    formSubmitErrorAlert = null;
    saving = true;
  }

  async function performAndClose(action: () => Promise<boolean>) {
    try {
      if (await action()) {
        modal.closeModal();
      } else {
        saving = false;
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        formSubmitErrorAlert = e.message;
        if (onvalidationerror(e)) {
          saving = false;
          return;
        }
      } else if (e instanceof Error) {
        formSubmitErrorAlert = e.message;
      } else {
        formSubmitErrorAlert = `${e}`;
      }
      saving = false;
      throw e;
    }
  }

  setContext<FormModalContext>('formModal', {
    saving: () => saving,
    endSaving: () => (saving = false),
    startSaving,
    closeModal: () => modal.closeModal(),
  });

  onMount(() => {
    modal.openModal();
  });
</script>

<Modal
  centered
  {scrollable}
  {size}
  id="{formId}_modal"
  title={header}
  titleSnippet={headerSnippet}
  onclose={() => {
    onclose();
    // invalidating the url after the end of the close animation because most of the time the invalidate causes the
    // modal to unmount before the animation is done, causing errors in the application.
    if (saving && invalidateUrl) {
      invalidate(invalidateUrl);
    }
  }}
  bind:this={modal}
>
  <form
    id={formId}
    onsubmit={async e => {
      e.preventDefault();
      if (!validationChecks()) {
        formSubmitErrorAlert = 'Please fix the following validation errors and try again.';
        return;
      }

      startSaving();
      await performAndClose(onsubmit);
    }}
  >
    {#if formSubmitErrorAlert}
      <Alert color="danger">{formSubmitErrorAlert}</Alert>
    {/if}
    {@render children?.()}
  </form>
  {#snippet footer(closeModal)}
    {#if showFooter}
      <div class="modal-footer" aria-live={confirmDelete ? 'polite' : undefined}>
        {#if confirmDelete}
          <span>{deleteConfirmText}</span>
          <div class="flex-grow-1"></div>
          <button class="btn btn-secondary" onclick={() => (confirmDelete = false)}>Cancel</button>
          <SpinnerButton
            color="danger"
            savingTitle="Deleting..."
            title={deleteBtnLabel}
            onClick={async () => {
              startSaving();
              await performAndClose(async () => {
                await onDelete?.();
                return true;
              });
            }}
          />
        {:else}
          <button class="btn btn-secondary" type="button" onclick={closeModal}>Cancel</button>
          {#if onDelete}
            <button class="btn btn-danger btn-outline" type="button" onclick={() => (confirmDelete = true)}>
              {deleteBtnLabel}
            </button>
          {/if}
          <SpinnerButton form={formId} savingTitle="Saving..." title={submitBtnLabel} submit />
        {/if}
      </div>
    {/if}
  {/snippet}
</Modal>
